import React, { useEffect, useRef, useState } from "react";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import toast from "react-hot-toast";

import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../../components/ui/dialog";
import { sendMailboxEmail } from "../api/mailboxApi";
import { resetMailboxCanSend } from "../hooks/useMailboxCanSend";

const BRAND = "#55C5D0";
const MAX_FILES = 10;
const MAX_TOTAL_BYTES = 20 * 1024 * 1024;

// What the backend's error codes mean to the agent.
const FAILURES = {
  reconnect_needed: "Your Gmail connection needs reconnecting. Open My Mailbox and connect again.",
  not_connected: "Your Gmail is not connected. Open My Mailbox to connect it.",
  send_disabled: "Sending from your mailbox is not switched on yet.",
  gmail_unavailable: "Gmail did not respond. Nothing was sent, please try again.",
  rejected: "Gmail rejected this email. Check the addresses and attachments.",
  uncertain: "Gmail did not confirm the send. Check your Gmail Sent folder before sending again.",
  unknown: "Something went wrong. Check your Gmail Sent folder before sending again.",
};

const newKey = () =>
  (window.crypto?.randomUUID?.() ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

const fieldClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#55C5D0] focus:outline-none";

const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;

/**
 * Write and send one email from the agent's own Gmail.
 * `initial` prefills { to, cc, bcc, subject, body } (e.g. from a mailto link).
 */
export default function ComposeEmailDialog({ open, onClose, initial }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCopies, setShowCopies] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const keyRef = useRef(newKey());
  const fileInput = useRef(null);

  useEffect(() => {
    if (!open) return;
    // A fresh email each time the composer opens; the key stays the same for
    // every retry of this one email.
    keyRef.current = newKey();
    setTo(initial?.to || "");
    setCc(initial?.cc || "");
    setBcc(initial?.bcc || "");
    setShowCopies(Boolean(initial?.cc || initial?.bcc));
    setSubject(initial?.subject || "");
    setBody(initial?.body || "");
    setFiles([]);
    setError("");
  }, [open, initial]);

  const totalBytes = files.reduce((n, f) => n + f.size, 0);

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list || [])];
    if (next.length > MAX_FILES) {
      setError(`Up to ${MAX_FILES} attachments.`);
      return;
    }
    if (next.reduce((n, f) => n + f.size, 0) > MAX_TOTAL_BYTES) {
      setError("Attachments can be up to 20 MB in total.");
      return;
    }
    setError("");
    setFiles(next);
  };

  const send = async () => {
    if (!to.trim()) return setError("Add at least one recipient.");
    if (!subject.trim()) return setError("Add a subject.");
    if (!body.trim()) return setError("Write a message.");
    setSending(true);
    setError("");
    const form = new FormData();
    form.append("client_key", keyRef.current);
    form.append("to", to);
    if (cc.trim()) form.append("cc", cc);
    if (bcc.trim()) form.append("bcc", bcc);
    form.append("subject", subject.trim());
    form.append("body", body);
    files.forEach((f) => form.append("attachments", f));
    try {
      const res = await sendMailboxEmail(form);
      if (res.status === "sent") {
        toast.success(res.duplicate ? "This email was already sent." : `Email sent from ${res.from_email}.`);
        onClose();
      } else {
        // Same key already sending, or possibly sent: never send it again from here.
        setError(FAILURES[res.error] || "This email is still sending. Check your Gmail Sent folder.");
      }
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.code === "reconnect_needed" || data.code === "not_connected") resetMailboxCanSend();
      if (data.code === "invalid" && data.errors) {
        setError(Object.values(data.errors).join(" "));
      } else if (data.code === "do_not_contact") {
        setError(data.detail);
      } else {
        setError(FAILURES[data.code] || "The email could not be sent. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New email</DialogTitle>
          <DialogDescription>Sent from your own Gmail. A copy stays in your Gmail Sent folder.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label htmlFor="compose-to" className="w-10 text-sm text-gray-500">To</label>
            <input id="compose-to" className={fieldClass} value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="name@example.com, another@example.com" />
            {!showCopies && (
              <button type="button" className="shrink-0 text-sm text-gray-500 hover:text-gray-800"
                onClick={() => setShowCopies(true)}>Cc / Bcc</button>
            )}
          </div>
          {showCopies && (
            <>
              <div className="flex items-center gap-2">
                <label htmlFor="compose-cc" className="w-10 text-sm text-gray-500">Cc</label>
                <input id="compose-cc" className={fieldClass} value={cc} onChange={(e) => setCc(e.target.value)} />
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="compose-bcc" className="w-10 text-sm text-gray-500">Bcc</label>
                <input id="compose-bcc" className={fieldClass} value={bcc} onChange={(e) => setBcc(e.target.value)} />
              </div>
            </>
          )}
          <input aria-label="Subject" className={fieldClass} value={subject} maxLength={250}
            onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
          <textarea aria-label="Message" className={`${fieldClass} min-h-[220px]`} value={body}
            onChange={(e) => setBody(e.target.value)} placeholder="Write your message" />

          {files.length > 0 && (
            <ul className="space-y-1">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-1.5 text-sm">
                  <span className="min-w-0 truncate">{f.name} <span className="text-gray-400">({formatSize(f.size)})</span></span>
                  <button type="button" aria-label={`Remove ${f.name}`} className="ml-2 text-gray-400 hover:text-gray-700"
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
              <li className="text-xs text-gray-400">{formatSize(totalBytes)} of 20 MB</li>
            </ul>
          )}

          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <div>
            <input ref={fileInput} type="file" multiple className="hidden" data-testid="compose-files"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            <button type="button" onClick={() => fileInput.current?.click()} disabled={sending}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <Paperclip className="h-4 w-4" /> Attach
            </button>
          </div>
          <button type="button" onClick={send} disabled={sending}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm text-white disabled:opacity-60"
            style={{ backgroundColor: BRAND }}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Sending…" : "Send"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
